"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HighVelocityCampaigns() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridRefVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const projects = [{
    id: 1,
    title: "Velocity Pulse",
    location: "GLOBAL SCALE",
    year: "2024",
    category: "PERFORMANCE",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_velocity_pulse.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_velocity_pulse.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_velocity_pulse.png-wvc-sizes",
    alt: "Velocity Pulse high-impact campaign",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]"
  }, {
    id: 2,
    title: "Neon Conversion",
    location: "TECH SECTOR",
    year: "2023",
    category: "GROWTH",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_neon_conversion.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_neon_conversion.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_neon_conversion.webp-wvc-sizes",
    alt: "Neon Conversion digital strategy",
    span: "lg:col-span-5",
    aspect: "aspect-[5/6]"
  }, {
    id: 3,
    title: "Data Catalyst",
    location: "FINTECH",
    year: "2024",
    category: "ANALYTICS",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_data_catalyst.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_data_catalyst.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_data_catalyst.webp-wvc-sizes",
    alt: "Data Catalyst analytics dashboard",
    span: "lg:col-span-5",
    aspect: "aspect-[5/6]"
  }, {
    id: 4,
    title: "Growth Engine",
    location: "E-COMMERCE",
    year: "2023",
    category: "SCALING",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/ecommerce_storefront.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/ecommerce_storefront.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/ecommerce_storefront.png-wvc-sizes",
    alt: "Growth Engine e-commerce scale",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]"
  }, {
    id: 5,
    title: "Brand Ignition",
    location: "SAAS",
    year: "2022",
    category: "IDENTITY",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_brand_ignition.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_brand_ignition.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_brand_ignition.png-wvc-sizes",
    alt: "Brand Ignition SaaS launch",
    span: "lg:col-span-6",
    aspect: "aspect-[4/3]"
  }, {
    id: 6,
    title: "ROI Matrix",
    location: "ENTERPRISE",
    year: "2024",
    category: "STRATEGY",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_roi_matrix.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_roi_matrix.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_roi_matrix.png-wvc-sizes",
    alt: "ROI Matrix enterprise strategy",
    span: "lg:col-span-6",
    aspect: "aspect-[4/3]"
  }];
  return <section data-section-id="506" id="high-velocity-campaigns" className="relative bg-background text-foreground py-24 md:py-32 lg:py-[120px] overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs tracking-widest text-primary uppercase">
                03 — Impact Portfolio
              </span>
              <span className="h-px w-12 bg-border" aria-hidden="true" />
            </div>
            <h2 className="font-default font-bold text-foreground leading-[1.05] tracking-tighter text-[clamp(2.5rem,5vw,4rem)]">
              High-Velocity Campaigns
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4 flex items-end">
            <p className="font-default text-xl text-muted-foreground leading-relaxed max-w-md">
              A collection of high-impact digital transformations — 
              where data-driven chemistry meets aggressive market growth. 
              Engineered for maximum ROI.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 transition-all duration-1000 ease-out delay-150 ${gridRefVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {projects.map((project, i) => <article data-index={i} key={project.id} className={`group ${project.span} relative`}>
              <Link to="/ContactPage" className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background" aria-label={`View ${project.title} campaign`}>
                {}
                <div className={`relative overflow-hidden border border-border ${project.aspect} bg-secondary`}>
                  <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-all duration-[600ms] ease-out group-hover:scale-[1.02] group-hover:saturate-[1.2]" />

                  {}
                  <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-[600ms] ease-out bg-primary/90 backdrop-blur-sm px-5 py-4 flex items-center justify-between">
                    <span className="font-mono text-[11px] tracking-widest text-primary-foreground uppercase">
                      {project.title.toUpperCase()} — {project.location}
                    </span>
                    <span className="font-mono text-[11px] tracking-widest text-primary-foreground/80">
                      {project.year}
                    </span>
                  </div>

                  {}
                  <div className="absolute top-4 left-4 font-mono text-[11px] tracking-widest text-white bg-black/40 backdrop-blur-sm px-2 py-1">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>

                {}
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-[10px] tracking-widest uppercase text-primary">
                        {project.category}
                      </span>
                      <span className="h-px flex-1 bg-border" aria-hidden="true" />
                      <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                        {project.year}
                      </span>
                    </div>
                    <h3 className="font-default font-bold text-foreground text-[22px] leading-tight transition-colors duration-300 group-hover:text-primary">
                      {project.title}
                    </h3>
                    <p className="font-mono text-[11px] tracking-wider uppercase text-muted-foreground mt-1">
                      {project.location}
                    </p>
                  </div>
                  <ArrowUpRight className="size-5 text-foreground/60 mt-1 transition-all duration-500 group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </div>
              </Link>
            </article>)}
        </div>

        {}
        <div className="mt-20 md:mt-28 flex flex-col items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="h-px w-16 bg-border" aria-hidden="true" />
            <span className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
              Archive
            </span>
            <span className="h-px w-16 bg-border" aria-hidden="true" />
          </div>
          <Button asChild size="lg" variant="default" className="h-12 px-8 font-mono text-xs tracking-widest uppercase bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/TheLabPage">
              View All Campaigns
              <ArrowRight className="size-4 ml-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}